/**
 * FileUploadPage — 파일 업로드 (Presigned URL + MinIO + 검증 파이프라인).
 *
 * 【역할】
 * - MinIO presigned URL 을 발급받아 클라이언트에서 직접 PUT 업로드
 * - 업로드 후 서버가 MIME + 확장자 + magic bytes 3중 검증 → verified/quarantined
 *
 * 【주요 기능】
 * - Dragger 로 다중 파일 업로드
 * - Presigned URL 발급 → MinIO 직접 PUT → 검증 trigger
 * - 파일별 진행 상태 표시 (uploading / verifying / verified / quarantined / error)
 * - 상태 컬러 태그 + Progress bar
 *
 * 【사용 훅】
 * - useLabel (t)
 * - useState (items[])
 *
 * 【Backend RPC serviceName】
 * - (REST 직호출) POST /api/upload/presign  — Presigned URL 발급
 * - (PUT) 발급된 MinIO presigned URL 로 직접 업로드
 *
 * 【DataSet 구조】
 * - 없음 (로컬 state UploadState[] 로 관리)
 *
 * 【사용처】 router/index.tsx — `{ path: 'files' }`
 * 【포팅 원본】 react-fastapi 신규 화면 (안전 업로드 데모)
 */
import { useState } from 'react'
import { Card, Progress, Space, Tag, Typography, Upload, message } from 'antd'
import { InboxOutlined } from '@ant-design/icons'
import type { UploadProps } from 'antd/es/upload/interface'
import http from '@/framework/api/axios'
import { useLabel } from '@/framework/useLabel'
import PageContainer from '@/components/common/PageContainer'

const { Title, Text } = Typography
const { Dragger } = Upload

interface UploadState {
  fileId: number
  name: string
  progress: number
  status: 'uploading' | 'verifying' | 'verified' | 'quarantined' | 'error'
  reason?: string
}

const STATUS_COLOR: Record<string, string> = {
  uploading: 'processing',
  verifying: 'processing',
  verified: 'success',
  quarantined: 'error',
  error: 'error'
}

/**
 * FileUploadPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function FileUploadPage() {
  const { t } = useLabel()
  const [items, setItems] = useState<UploadState[]>([])

  const uploadOne = async (file: File) => {
    // 1) presign
    const pres = await http.post('/upload/presign', {
      originalName: file.name,
      mime: file.type || 'application/octet-stream',
      sizeBytes: file.size
    })
    const { fileId, uploadUrl } = pres.data?.data ?? {}
    const entry: UploadState = {
      fileId,
      name: file.name,
      progress: 0,
      status: 'uploading'
    }
    setItems((xs) => [...xs, entry])

    // 2) 직접 MinIO PUT (presigned URL)
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.floor((e.loaded / e.total) * 90)
          setItems((xs) => xs.map((x) => (x.fileId === fileId ? { ...x, progress: pct } : x)))
        }
      }
      xhr.onload = () =>
        xhr.status >= 200 && xhr.status < 300 ? resolve() : reject(new Error(`PUT ${xhr.status}`))
      xhr.onerror = () => reject(new Error('network'))
      xhr.open('PUT', uploadUrl)
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
      xhr.send(file)
    })

    // 3) confirm (magic 검증)
    setItems((xs) =>
      xs.map((x) => (x.fileId === fileId ? { ...x, progress: 95, status: 'verifying' } : x))
    )
    const conf = await http.post(`/upload/confirm/${fileId}`)
    const { status, reason } = conf.data?.data ?? {}
    setItems((xs) =>
      xs.map((x) =>
        x.fileId === fileId
          ? {
              ...x,
              progress: 100,
              status: status === 'VERIFIED' ? 'verified' : 'quarantined',
              reason: reason ?? undefined
            }
          : x
      )
    )
    if (status === 'VERIFIED') {
      void message.success(t('MSG_FILE_VERIFIED', '파일 검증 완료.'))
    } else {
      void message.warning(t('MSG_FILE_QUARANTINED', '파일이 격리되었습니다 (검증 실패).'))
    }
  }

  const uploadProps: UploadProps = {
    multiple: true,
    beforeUpload: (file) => {
      uploadOne(file).catch((e) => {
        void message.error(`${file.name}: ${e.message}`)
      })
      return false
    },
    showUploadList: false
  }

  return (
    <PageContainer style={{ overflow: 'hidden' }}>
      <Title level={3} style={{ marginTop: 0 }}>
        {t('LBL_FILE_MGMT', '파일 관리')}
      </Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('LBL_DRAGGER_TEXT', '클릭 또는 드래그로 업로드')}</p>
          <p className="ant-upload-hint">
            {t('LBL_DRAGGER_HINT', '다중 파일 업로드를 지원합니다.')} (MIME + 확장자 + magic 3중
            검증)
          </p>
        </Dragger>
      </Card>

      <Card size="small" title={t('LBL_UPLOAD_PROGRESS', '업로드 진행')}>
        {items.length === 0 && (
          <Text type="secondary">{t('LBL_NO_UPLOADS', '업로드 내역 없음')}</Text>
        )}
        <Space direction="vertical" style={{ width: '100%' }}>
          {items.map((it) => (
            <div key={it.fileId} style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div
                style={{
                  width: 200,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {it.name}
              </div>
              <Progress
                percent={it.progress}
                style={{ flex: 1 }}
                status={
                  it.status === 'error' || it.status === 'quarantined' ? 'exception' : undefined
                }
              />
              <Tag color={STATUS_COLOR[it.status]}>{it.status.toUpperCase()}</Tag>
              {it.reason && (
                <Text type="danger" style={{ fontSize: 11 }}>
                  {it.reason}
                </Text>
              )}
            </div>
          ))}
        </Space>
      </Card>
    </PageContainer>
  )
}
